import { useState, useEffect } from 'react';
import { uploadRebusPhoto, validateRebusPhoto } from '../../../services/rebusImageService';
import { submitExtraTask } from '../../../services/rebusTeamService';
import PhotoCapture from '../common/PhotoCapture';

/**
 * Inline submission form for a single extra task.
 *
 * @param {Object} props
 * @param {Object} props.task - Extra task object { id, text, type, points }
 * @param {string} props.teamId - Current team ID
 * @param {string} props.sessionId - Current session ID
 * @param {Object} [props.existingResult] - Existing submission result
 * @param {function} props.onSubmit - Callback after successful submission
 */
export default function ExtraTaskSubmit({ task, teamId, sessionId, existingResult, onSubmit }) {
  const [textAnswer, setTextAnswer] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoComment, setPhotoComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isPhoto = task.type === 'photo';
  const isAlreadySubmitted = !!existingResult;

  // Create object URL for photo preview with proper cleanup
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!photoFile) {
      setPreviewUrl(photoPreview);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile, photoPreview]);

  const handlePhotoCapture = (file) => {
    const validation = validateRebusPhoto(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setPhotoFile(file);
    setError('');
  };

  const handlePhotoClear = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');

    if (isPhoto) {
      if (!photoFile) {
        setError('Du må ta eit bilete fyrst');
        return;
      }
    } else {
      if (!textAnswer.trim()) {
        setError('Skriv inn eit svar');
        return;
      }
    }

    setLoading(true);

    try {
      let submission = {};

      if (isPhoto) {
        const photoUrl = await uploadRebusPhoto(photoFile, sessionId, teamId, task.id);
        submission = { photoUrl };
        if (photoComment.trim()) {
          submission.comment = photoComment.trim();
        }
      } else {
        submission = { answer: textAnswer.trim() };
      }

      await submitExtraTask(sessionId, teamId, task.id, submission);
      setSuccess(true);
      onSubmit?.(task.id);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('allereie levert')) {
        setError('Denne oppgåva er allereie levert');
      } else if (msg.includes('ikkje starta')) {
        setError('Mikkelchase er ikkje starta enno. Vent på at admin startar.');
      } else if (msg.includes('avslutta')) {
        setError('Mikkelchase er avslutta. Du kan ikkje levere fleire oppgåver.');
      } else {
        setError('Klarte ikkje levere. Prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Already submitted — show status (but allow resubmit if rejected)
  const isRejected = existingResult?.approved === false;
  if ((isAlreadySubmitted && !isRejected) || success) {
    const result = existingResult || {};
    return (
      <div className="px-4 py-3 bg-emerald-50 shadow-ambient rounded-xl">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
          <span className="material-symbols-outlined text-lg">check_circle</span>
          <span>
            {result.approved === true
              ? `Godkjent! +${result.points || 0} poeng`
              : 'Levert — ventar på vurdering'}
          </span>
        </div>
        {result.adminFeedback && (
          <div className="mt-2 border-l-4 border-amber-400 bg-amber-50 rounded-r-lg px-3 py-2">
            <p className="text-xs text-amber-600 font-semibold mb-0.5">Tilbakemelding frå admin</p>
            <p className="text-sm text-gray-800 italic">{result.adminFeedback}</p>
          </div>
        )}
        {(result.comment || photoComment) && (
          <p className="mt-2 text-sm text-emerald-600 italic">Kommentar: {result.comment || photoComment}</p>
        )}
        {result.photoUrl && (
          <img
            src={result.photoUrl}
            alt="Innsendt bilete"
            className="mt-2 w-full h-32 object-cover rounded-lg"
          />
        )}
        {result.answer && (
          <p className="mt-1 text-sm text-emerald-600 italic">Svar: {result.answer}</p>
        )}
      </div>
    );
  }

  // Show rejection notice with resubmit form
  if (isRejected) {
    // Fall through to the regular form below, but show rejection notice first
  }

  return (
    <div className="space-y-3">
      {isRejected && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-sm font-semibold">
            <span className="material-symbols-outlined text-lg">close</span>
            <span>Avvist — send inn på nytt</span>
          </div>
          {existingResult?.adminFeedback && (
            <div className="border-l-4 border-amber-400 bg-amber-50 rounded-r-xl px-4 py-3">
              <p className="text-xs text-amber-600 font-semibold mb-0.5">Tilbakemelding frå admin</p>
              <p className="text-sm text-gray-800 italic">{existingResult.adminFeedback}</p>
            </div>
          )}
          {existingResult?.answer && (
            <p className="text-xs text-gray-500 italic">
              Ditt førre svar: <span className="font-semibold text-gray-700">{existingResult.answer}</span>
            </p>
          )}
          {existingResult?.photoUrl && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Ditt førre bilete:</p>
              <img src={existingResult.photoUrl} alt="Førre innlevering" className="w-full h-24 object-cover rounded-lg opacity-60" />
            </div>
          )}
        </div>
      )}
      {isPhoto ? (
        <>
          <PhotoCapture
            onCapture={handlePhotoCapture}
            preview={previewUrl}
            onClear={handlePhotoClear}
          />
          {photoFile && (
            <textarea
              value={photoComment}
              onChange={(e) => setPhotoComment(e.target.value)}
              placeholder="Legg til ein kommentar (valfritt)..."
              rows={2}
              maxLength={300}
              disabled={loading}
              className="w-full px-4 py-3 rounded-xl ghost-border ghost-border-focus bg-gray-50 text-gray-900 placeholder:text-gray-400 font-medium transition-all resize-none text-sm"
            />
          )}
        </>
      ) : (
        <input
          type="text"
          value={textAnswer}
          onChange={(e) => {
            setTextAnswer(e.target.value);
            setError('');
          }}
          placeholder="Skriv svaret ditt..."
          disabled={loading}
          className="w-full px-4 py-3 rounded-xl ghost-border ghost-border-focus bg-gray-50 text-gray-900 placeholder:text-gray-400 font-medium transition-all"
        />
      )}

      {error && (
        <p className="text-red-600 text-sm font-medium">{error}</p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || (isPhoto ? !photoFile : !textAnswer.trim())}
        className="w-full h-12 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white font-bold rounded-xl shadow-ambient active:scale-[0.97] transition-all disabled:opacity-40 disabled:active:scale-100 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
            <span>{isPhoto ? 'Lastar opp...' : 'Sender...'}</span>
          </>
        ) : (
          <>
            <span className="material-symbols-outlined text-xl">{isPhoto ? 'cloud_upload' : 'send'}</span>
            <span>{isPhoto ? 'Last opp' : 'Send svar'}</span>
          </>
        )}
      </button>
    </div>
  );
}
